import { createPresenceClient } from "../shared/presence-client.js";
import { mountStrip } from "../shared/strip.js";
import { createSim, step, resetTime } from "./sim.js";
import { createRenderer } from "./render.js";
import { mountPanel } from "./panel.js";

window.addEventListener("load", () => {
  const presence = createPresenceClient();
  mountStrip(presence);
  presence.connect();
});

const canvas = document.getElementById("cv");
const panel = document.getElementById("panel");

const view = { yaw: 0.45, pitch: -0.30, autoYaw: 0.0015, autoRotate: true, labels: true };
const sim = createSim();
const renderer = createRenderer(canvas, view);

const MODE_KEY = "dst.timespace.mode";
let mode = localStorage.getItem(MODE_KEY) === "advanced" ? "advanced" : "simple";

function rebuildSliderCache() {
  for (const key of Object.keys(sim.magnets)) {
    const card = panel.querySelector(`.axis[data-key="${key}"]`);
    sliderCache[key] = {
      card,
      speed: card?.querySelector('[data-control="speed"]'),
      force: card?.querySelector('[data-control="force"]'),
    };
  }
}

function mountForMode() {
  mountPanel(panel, sim, mode);
  rebuildSliderCache();
  const modeBtn = document.getElementById("mode-toggle");
  if (modeBtn) modeBtn.textContent = mode === "advanced" ? "Simple" : "Advanced";
}

const sliderCache = {};
mountForMode();

// View toggles + time controls (all live in .ts-controls below the canvas).
const labelsBtn = document.getElementById("labels-toggle");
const rotateBtn = document.getElementById("rotate-toggle");
const resetBtn  = document.getElementById("reset-time");
const pauseBtn  = document.getElementById("toggle-pause");
const modeBtn   = document.getElementById("mode-toggle");

function syncTopBtns() {
  if (labelsBtn) { labelsBtn.textContent = (view.labels ? "◉" : "○") + " Labels"; labelsBtn.classList.toggle("off", !view.labels); }
  if (rotateBtn) { rotateBtn.textContent = (view.autoRotate ? "⟳" : "⊘") + " Auto-rotate"; rotateBtn.classList.toggle("off", !view.autoRotate); }
}
syncTopBtns();
labelsBtn?.addEventListener("click", () => { view.labels = !view.labels; syncTopBtns(); });
rotateBtn?.addEventListener("click", () => { view.autoRotate = !view.autoRotate; syncTopBtns(); });
resetBtn?.addEventListener("click", () => resetTime(sim));
pauseBtn?.addEventListener("click", () => {
  sim.paused = !sim.paused;
  pauseBtn.textContent = sim.paused ? "Resume" : "Pause";
});

// drag to rotate
let dragging = false, lastX = 0, lastY = 0;
canvas.addEventListener("pointerdown", (e) => {
  dragging = true; lastX = e.clientX; lastY = e.clientY;
  canvas.setPointerCapture(e.pointerId);
  view.autoRotate = false;
  syncTopBtns();
});
canvas.addEventListener("pointermove", (e) => {
  if (!dragging) return;
  view.yaw   += (e.clientX - lastX) * 0.005;
  view.pitch += (e.clientY - lastY) * 0.005;
  view.pitch = Math.max(-1.4, Math.min(1.4, view.pitch));
  lastX = e.clientX; lastY = e.clientY;
});
canvas.addEventListener("pointerup",   () => { dragging = false; });
canvas.addEventListener("pointercancel", () => { dragging = false; });

function reflectOscillation() {
  const active = document.activeElement;
  for (const [key, m] of Object.entries(sim.magnets)) {
    const c = sliderCache[key];
    if (!c) continue;
    const card = c.card;
    if (!card) continue;
    const speedOsc = m.speed.osc;
    const forceOsc = m.force.osc;
    card.classList.toggle("is-oscillating", speedOsc || forceOsc);

    // Simple mode: c.speed / c.force are the single sliders bound to high (low = -high).
    if (c.speed && c.speed !== active && speedOsc) c.speed.value = m.effSpeed;
    if (c.force && c.force !== active && forceOsc) c.force.value = m.effForce;

    // Advanced mode: high-handle reflects the current effective value.
    const speedHi = card.querySelector('[data-control="speed-hi"]');
    const forceHi = card.querySelector('[data-control="force-hi"]');
    if (speedHi && speedHi !== active && speedOsc) speedHi.value = m.effSpeed;
    if (forceHi && forceHi !== active && forceOsc) forceHi.value = m.effForce;
  }
}

modeBtn?.addEventListener("click", () => {
  mode = mode === "advanced" ? "simple" : "advanced";
  localStorage.setItem(MODE_KEY, mode);
  mountForMode();
});

let rafId = 0;
function frame() {
  if (view.autoRotate) view.yaw += view.autoYaw;
  step(sim);
  renderer.render(sim);
  reflectOscillation();
  rafId = requestAnimationFrame(frame);
}

document.addEventListener("visibilitychange", () => {
  if (document.hidden) cancelAnimationFrame(rafId);
  else rafId = requestAnimationFrame(frame);
});

rafId = requestAnimationFrame(frame);
